import { useEffect, useRef, useState } from "react";
import api, { money, errMsg } from "../lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, Trash2, PlusCircle, Link2 } from "lucide-react";
import DatePicker from "@/components/ui/DatePicker";
import SearchableSelect from "../components/ui/SearchableSelect";
import { todayIST } from "../lib/datetime";
import useAutoRefresh from "../lib/useAutoRefresh";

const role = () => {
  try {
    return (JSON.parse(localStorage.getItem("lss_user") || "{}").role || "owner").toLowerCase();
  } catch {
    return "owner";
  }
};

export default function POSUpload() {
  // Reset wipes every POS transaction and reverses the stock deductions they
  // caused — owner-only at the API. The button used to be rendered for
  // managers regardless, so it answered 403 instead of being absent.
  const isOwner = role() === "owner";
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [txns, setTxns] = useState([]);
  const [dates, setDates] = useState([]);
  const [day, setDay] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [unmappedItems, setUnmappedItems] = useState([]);
  const [skus, setSkus] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [mappingMode, setMappingMode] = useState("link"); // "link" | "new"
  const [selectedPosItem, setSelectedPosItem] = useState("");
  const [selectedSkuId, setSelectedSkuId] = useState("");
  const [mapBusy, setMapBusy] = useState(false);
  const [zeroStockSales, setZeroStockSales] = useState([]);
  // Owner, 30 Oct 2026: land on the Flag Table — it IS the list of retail
  // items to map after an upload. (The old default, "zero_stock", had no
  // rendering block at all, so the card opened blank.)
  const [posTab, setPosTab] = useState("flags");
  const [flags, setFlags] = useState([]);
  const [showResolved, setShowResolved] = useState(false);

  const [newItemForm, setNewItemForm] = useState({
    name: "",
    category: "Retail",
    brand: "",
    vendor_id: "",
    unit_cost: 0,
    mrp: 0,
    selling_price: 0,
    unit_price: 0,
    store_qty: 0,
  });
  const inputRef = useRef(null);

  const handleResetPOS = async () => {
    if (!window.confirm("Are you sure you want to reset and clear ALL POS transactions? This action is destructive and cannot be undone.")) {
      return;
    }
    setBusy(true);
    try {
      await api.post("/pos/reset");
      toast.success("All POS transactions reset successfully.");
      setTxns([]);
      setDates([]);
      setDay(null);
      setUnmappedItems([]);
      setZeroStockSales([]);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const loadDates = () =>
    api.get("/pos/dates")
      .then((r) => {
        setDates(r.data);
        if (!day && r.data[0]) setDay(r.data[0]);
      })
      .catch((e) => toast.error(errMsg(e)));

  const [allPosProducts, setAllPosProducts] = useState([]);

  const loadUnmapped = () => {
    api.get("/pos/unmapped-items")
      .then((r) => setUnmappedItems(r.data || []))
      .catch(() => {});
    api.get("/pos/product-items")
      .then((r) => setAllPosProducts(r.data || []))
      .catch(() => {});
  };

  const loadZeroStock = (d) => {
    const targetDate = d || day;
    const url = targetDate ? `/pos/zero-stock-sales?date=${targetDate}` : "/pos/zero-stock-sales?all_dates=true";
    api.get(url)
      .then((r) => {
        const resList = r.data || [];
        if (resList.length > 0) {
          setZeroStockSales(resList);
        } else {
          api.get("/pos/zero-stock-sales?all_dates=true")
            .then((r2) => setZeroStockSales(r2.data || []))
            .catch(() => {});
        }
      })
      .catch(() => {});
  };

  const loadFlags = () => {
    api.get("/pos/flags")
      .then((r) => setFlags(r.data || []))
      .catch(() => {});
  };

  const [mappings, setMappings] = useState([]);
  const loadMappings = () => {
    api.get("/pos/mappings")
      .then((r) => setMappings(r.data || []))
      .catch(() => {});
  };

  const resolveFlag = async (flag) => {
    // kept for API parity; manual resolve button removed from flag rows —
    // rows now resolve only via mapping, restock, or re-upload deduction
    try {
      const res = await api.post(`/pos/flags/${flag.id}/resolve`);
      toast.success(res.data.message || "Flag resolved.");
      loadFlags();
      loadZeroStock();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    loadDates();
    loadUnmapped();
    loadZeroStock();
    loadFlags();
    loadMappings();
    api.get("/inventory/skus").then((r) => setSkus(r.data || [])).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the flag, zero-stock, unmapped and mapping lists current when a
  // colleague uploads in another tab. Every one of these loaders already
  // swallows its own error, so a failed background pull cannot pop a toast,
  // and none of them touch a loading flag — nothing on screen blinks.
  // Paused while an upload is in flight or the mapping modal is open: the
  // item being mapped is chosen from unmappedItems, and replacing that list
  // underneath the modal would strand the selection.
  useAutoRefresh(() => {
    if (busy || isMapModalOpen) return;
    loadUnmapped();
    loadZeroStock(day);
    loadFlags();
    loadMappings();
  }, 30000);

  useEffect(() => {
    if (!day) return setTxns([]);
    api.get(`/pos/transactions?date=${day}`)
      .then((r) => setTxns(r.data))
      .catch((e) => toast.error(errMsg(e)));
    loadZeroStock(day);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  const upload = async () => {
    if (!file) return toast.error("Choose a CSV first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/pos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const autoDeducted = r.data.pos_auto_checkouts || 0;
      toast.success(
        `Imported ${r.data.imported} rows · ${r.data.quality_failures} quality flags · ${autoDeducted} retail item${autoDeducted === 1 ? "" : "s"} auto-deducted.`
      );
      if (r.data.unmapped_items && r.data.unmapped_items.length > 0) {
        toast.warning(`${r.data.unmapped_items.length} unmapped item(s) queued for mapping in Flag Table.`);
      }
      if (r.data.zero_stock_sales && r.data.zero_stock_sales.length > 0) {
        setZeroStockSales(r.data.zero_stock_sales);
      } else {
        setZeroStockSales([]);
      }
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      await loadDates();
      await loadUnmapped();
      await loadFlags();
      loadMappings();
      if (day) {
        api.get(`/pos/transactions?date=${day}`).then((r) => setTxns(r.data)).catch(() => {});
        loadZeroStock(day);
      } else {
        loadZeroStock();
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const handleSaveMapping = async () => {
    if (!selectedPosItem || !selectedSkuId) {
      return toast.error("Select both POS Item and Master SKU");
    }
    setMapBusy(true);
    try {
      const res = await api.post("/pos/map-item", {
        pos_item_name: selectedPosItem,
        target_sku_id: selectedSkuId,
      });
      toast.success(res.data.message || "Mapping saved successfully!");
      setIsMapModalOpen(false);
      setSelectedPosItem("");
      setSelectedSkuId("");
      await loadUnmapped();
      await loadFlags();
      loadMappings();
      await loadZeroStock();
      if (day) {
        const r = await api.get(`/pos/transactions?date=${day}`);
        setTxns(r.data);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setMapBusy(false);
    }
  };

  const handleCreateNewSkuAndMap = async () => {
    if (!selectedPosItem) return toast.error("Select a POS Item");
    if (!newItemForm.name.trim()) return toast.error("Enter Official SKU Name");

    setMapBusy(true);
    try {
      const selectedVendor = vendors.find(v => v.id === newItemForm.vendor_id);
      const payload = {
        pos_item_name: selectedPosItem,
        name: newItemForm.name.trim(),
        category: newItemForm.category || "Retail",
        brand: newItemForm.brand || "",
        vendor_id: newItemForm.vendor_id || null,
        vendor_name: selectedVendor ? selectedVendor.name : "",
        unit_cost: parseFloat(newItemForm.unit_cost) || 0,
        mrp: parseFloat(newItemForm.mrp) || 0,
        selling_price: parseFloat(newItemForm.selling_price) || parseFloat(newItemForm.mrp) || 0,
        unit_price: parseFloat(newItemForm.unit_price) || parseFloat(newItemForm.selling_price) || parseFloat(newItemForm.mrp) || 0,
        store_qty: parseFloat(newItemForm.store_qty) || 0,
      };

      const res = await api.post("/pos/create-and-map-sku", payload);
      toast.success(res.data.message || "New Item registered and mapped successfully!");
      setIsMapModalOpen(false);
      setSelectedPosItem("");
      setSelectedSkuId("");
      await loadUnmapped();
      await loadFlags();
      loadMappings();
      await loadZeroStock();
      await loadSkus();
      if (day) {
        const r = await api.get(`/pos/transactions?date=${day}`);
        setTxns(r.data);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setMapBusy(false);
    }
  };

  const openMapModal = (posItemName = "") => {
    const itemToMap = posItemName || selectedPosItem || unmappedItems[0] || "";
    setSelectedPosItem(itemToMap);
    setSelectedSkuId("");
    setNewItemForm((prev) => ({
      ...prev,
      name: itemToMap,
    }));
    setIsMapModalOpen(true);
  };


  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto space-y-8">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <div className="lss-overline">POS Import & Sync</div>
          <h1 className="font-serif-lux text-3xl sm:text-4xl font-bold text-slate-950 tracking-tight mt-1">Bring in the Day's Sales</h1>
          <p className="text-slate-600 font-medium text-sm sm:text-base mt-2 max-w-2xl">
            Upload your Geetanjali Excel (.xlsx, .xls) or CSV export file. Columns are automatically parsed, staff & SKUs seeded, and full-discount services tagged for quality audit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => openMapModal()}
            className="bg-amber-900 hover:bg-amber-950 text-white font-extrabold px-4 py-2.5 text-xs uppercase tracking-wider rounded-xl transition-all inline-flex items-center gap-1.5 shadow-sm shrink-0"
          >
            <Link2 className="w-4 h-4 text-amber-400" />
            Map POS Items
          </button>
          {isOwner && (
            <button
              onClick={handleResetPOS}
              disabled={busy}
              title="Delete ALL POS transactions and reverse their stock deductions"
              className="bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 hover:border-red-300 px-4 py-2.5 text-xs font-bold uppercase tracking-wider rounded-xl transition-all inline-flex items-center gap-1.5 shadow-xs disabled:opacity-50 shrink-0"
            >
              <Trash2 className="w-4.5 h-4.5" />
              Reset POS Data
            </button>
          )}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="lss-card p-6 sm:p-8 max-w-4xl border-t-4 border-amber-500"
      >
        <motion.label
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          data-testid="upload-dropzone"
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            if (e.dataTransfer.files && e.dataTransfer.files[0]) {
              setFile(e.dataTransfer.files[0]);
            }
          }}
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl py-12 px-6 cursor-pointer text-center transition-all ${
            isDragging ? "border-amber-500 bg-amber-100/80 scale-105" :
            file
              ? "border-amber-400 bg-amber-50/60"
              : "border-slate-300 bg-slate-50/70 hover:bg-slate-100/80 hover:border-amber-400"
          }`}
        >
          <div className="p-3 bg-amber-100 text-amber-900 rounded-2xl mb-4 shadow-xs">
            {file ? <FileSpreadsheet className="w-8 h-8 text-amber-800" /> : <UploadCloud className="w-8 h-8 text-amber-700" />}
          </div>
          
          <div className="text-lg font-extrabold text-slate-950">
            {file ? file.name : "Drop CSV or Excel (.xlsx, .xls) file here or click to browse"}
          </div>

          <div className="text-xs text-slate-600 font-semibold mt-1 max-w-md">
            Supports .xlsx, .xls, and .csv exports (Expected headers: Date · Item Name · Net Price · Staff 1-4)
          </div>

          <input
            ref={inputRef}
            data-testid="upload-input"
            type="file"
            accept=".csv, .xlsx, .xls, .xlsm, .xlsb, .ods, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv, text/plain"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0])}
          />
        </motion.label>

        <div className="flex justify-between items-center mt-6">
          <div className="text-xs text-slate-500 font-semibold flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Auto-seeds new staff &amp; items · mapped retail stock auto-deducted from inventory
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="upload-submit"
              disabled={busy || !file}
              onClick={upload}
              className="lss-btn-gold px-8 py-3 uppercase tracking-wider text-xs font-extrabold disabled:opacity-50 shadow-md"
            >
              {busy ? "Processing Data…" : "Process & Sync POS Data"}
            </button>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="lss-card p-6 border-l-4 border-amber-500 bg-amber-50/20 rounded-xl space-y-4 shadow-sm"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-amber-200/80 pb-4">
          <div>
<h3 className="font-serif-lux text-xl font-bold text-slate-950">POS Product Inventory & Mapping Review</h3>
              <p className="text-xs text-slate-600 font-medium mt-0.5">Flag Table = every sale with mapping actions. Mapped Items = saved links reused automatically on every future upload — map once, never again.</p>
          </div>
          <div className="flex bg-amber-100/70 p-1 rounded-xl gap-1 shrink-0">
            <button
              onClick={() => setPosTab("flags")}
              className={`px-3.5 py-1.5 text-xs font-extrabold uppercase tracking-wider rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                posTab === "flags"
                  ? "bg-amber-900 text-white shadow-xs"
                  : "text-amber-950 hover:bg-amber-200/60"
              }`}
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              Flag Table ({flags.filter(f => f.status === "open").length})
            </button>
            <button
              onClick={() => setPosTab("mapped")}
              className={`px-3.5 py-1.5 text-xs font-extrabold uppercase tracking-wider rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                posTab === "mapped"
                  ? "bg-amber-900 text-white shadow-xs"
                  : "text-amber-950 hover:bg-amber-200/60"
              }`}
            >
              <Link2 className="w-3.5 h-3.5" />
              Mapped Items ({mappings.length})
            </button>
          </div>
        </div>

        {posTab === "flags" && (
          <div>
            <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
              <p className="text-slate-700 text-xs sm:text-sm font-medium">
                Upload audit: every POS product row with an <span className="font-bold">unmapped</span> name gets a flag here — map it once, and pending stock is auto-deducted and remembered for all future uploads. Rows with <span className="font-bold">low stock</span> are reported on the upload toast. <span className="font-bold">Resolved</span> flags were deducted successfully or fixed later.
              </p>
              <label className="flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={showResolved}
                  onChange={(e) => setShowResolved(e.target.checked)}
                  className="accent-amber-700"
                />
                Show resolved
              </label>
            </div>
            {(() => {
              const visible = flags.filter(f => showResolved || f.status === "open");
              return visible.length > 0 ? (
                <div className="overflow-x-auto rounded-xl border border-amber-200 bg-white shadow-2xs">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-amber-100/70 text-amber-950 font-bold border-b border-amber-200 uppercase tracking-wider">
                      <tr>
                        <th className="px-3.5 py-3">Status</th>
                        <th className="px-3.5 py-3">Date</th>
                        <th className="px-3.5 py-3">Invoice #</th>
                        <th className="px-3.5 py-3">POS Item Name</th>
                        <th className="px-3.5 py-3 text-center">Qty Sold</th>
                        <th className="px-3.5 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100 font-medium">
                      {visible.map((item, idx) => {
                        const isOpen = item.status !== "resolved";
                        return (
                          <tr key={idx} className={isOpen ? "hover:bg-amber-50/50 transition-colors" : "bg-slate-50/60 hover:bg-slate-100/60 transition-colors"}>
                            <td className="px-3.5 py-2.5">
                              <span className={`px-2 py-0.5 rounded-md text-[11px] font-extrabold border ${
                                item.reason === "unmapped" && isOpen
                                  ? "bg-purple-100 text-purple-900 border-purple-300"
                                  : item.reason === "insufficient_stock" && isOpen
                                    ? "bg-rose-100 text-rose-900 border-rose-300"
                                    : "bg-emerald-100 text-emerald-900 border-emerald-300"
                              }`}>
                                {isOpen ? (item.reason === "unmapped" ? "UNMAPPED" : "LOW STOCK") : "DEDUCTED"}
                              </span>
                            </td>
                            <td className="px-3.5 py-2.5 font-mono font-bold text-slate-700">{item.date || "—"}</td>
                            <td className="px-3.5 py-2.5 font-mono font-bold text-slate-800">{item.invoice_number || "POS Sale"}</td>
                            <td className="px-3.5 py-2.5">
                              <div className="font-semibold text-slate-950">{item.item_name}</div>
                              {item.sku_name && item.sku_name.toLowerCase().trim() !== item.item_name.toLowerCase().trim() && (
                                <div className="text-[11px] text-emerald-700 font-medium flex items-center gap-1 mt-0.5">
                                  <span className="text-slate-400">&rarr;</span> {item.sku_name}
                                </div>
                              )}
                            </td>
                            <td className="px-3.5 py-2.5 text-center font-bold text-amber-900">{item.requested_qty}</td>
                            <td className="px-3.5 py-2.5 text-right">
                              {isOpen ? (
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={() => openMapModal(item.item_name)}
                                    className="bg-amber-100 hover:bg-amber-200 text-amber-950 font-bold text-[11px] uppercase tracking-wider px-2.5 py-1.5 rounded-lg border border-amber-300 transition-all flex items-center gap-1 shrink-0 cursor-pointer"
                                  >
                                    <Link2 className="w-3.5 h-3.5" />
                                    Map
                                  </button>
                                </div>
                              ) : (
                                <span className="text-[11px] font-bold text-emerald-700 uppercase">
                                  {item.reason === "unmapped" ? "Mapped & deducted" : item.reason === "insufficient_stock" ? "Restocked & resolved" : "Auto-deducted"} ✓
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-white rounded-xl border border-amber-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    <div>
                      <div className="font-extrabold text-slate-900 text-sm">No POS Stock Flags</div>
                      <div className="text-xs text-slate-600 font-medium">Every product name in this upload matched a Master SKU — retail stock was auto-deducted from inventory.</div>
                    </div>
                  </div>
                  <button
                    onClick={() => openMapModal()}
                    className="bg-amber-900 hover:bg-amber-950 text-white font-extrabold text-xs uppercase tracking-wider px-4 py-2 rounded-xl transition-all shadow-2xs shrink-0 flex items-center gap-1.5 cursor-pointer"
                  >
                    <Link2 className="w-3.5 h-3.5 text-amber-400" /> Open Product Mapping Window
                  </button>
                </div>
              );
            })()}
          </div>
        )}

        {posTab === "mapped" && (
          <div>
            <p className="text-slate-700 text-xs sm:text-sm font-medium mb-3">
              Saved POS name &rarr; Master SKU links. Every future upload reuses these automatically, so the name never needs mapping again and retail stock is auto-deducted immediately. Wrong link? Delete it and map fresh from the Flag Table.
            </p>
            {mappings.length > 0 ? (
              <div className="overflow-x-auto rounded-xl border border-amber-200 bg-white shadow-2xs">
                <table className="w-full text-left text-xs">
                  <thead className="bg-amber-100/70 text-amber-950 font-bold border-b border-amber-200 uppercase tracking-wider">
                    <tr>
                      <th className="px-3.5 py-3">POS Item Name</th>
                      <th className="px-3.5 py-3">Linked Master SKU</th>
                      <th className="px-3.5 py-3 text-center">Current Stock</th>
                      <th className="px-3.5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-100 font-medium">
                    {mappings.map((m) => (
                      <tr key={m.id} className="hover:bg-amber-50/50 transition-colors">
                        <td className="px-3.5 py-2.5 font-mono font-bold text-slate-950">{m.pos_item_name}</td>
                        <td className="px-3.5 py-2.5 text-slate-700">
                          {m.sku_missing ? (
                            <span className="text-rose-700 italic font-bold">SKU deleted — remap</span>
                          ) : (
                            <span className="font-bold text-slate-900">{m.sku_name || m.display_name}</span>
                          )}
                        </td>
                        <td className="px-3.5 py-2.5 text-center font-extrabold text-slate-800">{m.current_stock} Pcs</td>
                        <td className="px-3.5 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openMapModal(m.pos_item_name)}
                              className="bg-amber-100 hover:bg-amber-200 text-amber-950 font-bold text-[11px] uppercase tracking-wider px-2.5 py-1.5 rounded-lg border border-amber-300 transition-all flex items-center gap-1 shrink-0 cursor-pointer"
                            >
                              <Link2 className="w-3.5 h-3.5" />
                              Re-Map
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-white rounded-xl border border-amber-200">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                  <div>
                    <div className="font-extrabold text-slate-900 text-sm">No Saved Mappings Yet</div>
                    <div className="text-xs text-slate-600 font-medium">Map items from the Flag Table once — they appear here and auto-apply on all future uploads.</div>
                  </div>
                </div>
                <button
                  onClick={() => openMapModal()}
                  className="bg-amber-900 hover:bg-amber-950 text-white font-extrabold text-xs uppercase tracking-wider px-4 py-2 rounded-xl transition-all shadow-2xs shrink-0 flex items-center gap-1.5 cursor-pointer"
                >
                  <Link2 className="w-3.5 h-3.5 text-amber-400" /> Open Product Mapping Window
                </button>
              </div>
            )}
          </div>
        )}
      </motion.div>


      {isMapModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 overflow-y-auto max-h-screen" onClick={() => setIsMapModalOpen(false)}>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-6 sm:p-8 max-w-xl w-full shadow-2xl border border-slate-200 space-y-5 my-8"
           onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="font-serif-lux text-xl font-bold text-slate-950">Map or Register Unmatched Product</h3>
              <p className="text-xs text-slate-600 font-medium mt-1">
                Link a POS item name to an existing Master SKU or register it as a brand new item with full vendor & stock details.
              </p>
            </div>

            {/* Mode Tabs */}
            <div className="flex bg-slate-100 p-1 rounded-xl gap-1">
              <button
                type="button"
                onClick={() => setMappingMode("link")}
                className={`flex-1 py-2 text-xs font-extrabold uppercase tracking-wider rounded-lg transition-all ${
                  mappingMode === "link" ? "bg-white text-slate-950 shadow-xs" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Link to Existing SKU
              </button>
              <button
                type="button"
                onClick={() => {
                  setMappingMode("new");
                  if (selectedPosItem && !newItemForm.name) {
                    setNewItemForm(prev => ({ ...prev, name: selectedPosItem }));
                  }
                }}
                className={`flex-1 py-2 text-xs font-extrabold uppercase tracking-wider rounded-lg transition-all ${
                  mappingMode === "new" ? "bg-white text-slate-950 shadow-xs" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                + Register as New Master SKU
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-extrabold uppercase tracking-wider text-slate-700 mb-1">
                  POS Item Name to Map *
                </label>
                {unmappedItems.length > 0 || allPosProducts.length > 0 || selectedPosItem ? (
                  <select
                    value={selectedPosItem}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedPosItem(val);
                      setNewItemForm((prev) => ({ ...prev, name: val }));
                    }}
                    className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm font-extrabold bg-slate-50 text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none"
                  >
                    {!selectedPosItem && <option value="">-- Select POS Product Item Name --</option>}
                    {Array.from(new Set([...(selectedPosItem ? [selectedPosItem] : []), ...unmappedItems, ...allPosProducts])).map((name) => (
                      <option key={name} value={name}>
                        {name} {unmappedItems.includes(name) ? " (Unmapped)" : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={selectedPosItem}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedPosItem(val);
                      setNewItemForm((prev) => ({ ...prev, name: val }));
                    }}
                    placeholder="Enter POS Item Name to Map..."
                    className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm font-extrabold bg-white text-slate-900 focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                )}
              </div>

              {mappingMode === "link" ? (
                <div>
                  <label className="block text-xs font-extrabold uppercase tracking-wider text-slate-700 mb-1">
                    Target Master SKU (Inventory)
                  </label>
                  <SearchableSelect
                    options={skus}
                    value={selectedSkuId}
                    onChange={(val) => setSelectedSkuId(val)}
                    placeholder="Search or Select Master SKU..."
                    getOptionLabel={(s) => {
                      const total = s.current_stock !== undefined ? s.current_stock : (s.store_qty || 0);
                      return `${s.name} (${s.brand || "General"}) — Stock: ${total} Pcs`;
                    }}
                    getOptionValue={(s) => s.id}
                  />
                </div>
              ) : (
                <div className="space-y-3 bg-slate-50/70 p-4 rounded-xl border border-slate-200 max-h-96 overflow-y-auto">
                  <div className="text-xs font-extrabold text-amber-900 uppercase tracking-wider">New Product Details</div>
                  
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Official SKU Name *</label>
                    <input
                      type="text"
                      value={newItemForm.name}
                      onChange={(e) => setNewItemForm({ ...newItemForm, name: e.target.value })}
                      placeholder="e.g. Loreal Serie Expert Shampoo 300ml"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-semibold bg-white outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Category</label>
                      <input
                        type="text"
                        value={newItemForm.category}
                        onChange={(e) => setNewItemForm({ ...newItemForm, category: e.target.value })}
                        placeholder="Retail"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-semibold bg-white outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Brand</label>
                      <input
                        type="text"
                        value={newItemForm.brand}
                        onChange={(e) => setNewItemForm({ ...newItemForm, brand: e.target.value })}
                        placeholder="L'Oreal"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-semibold bg-white outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Vendor / Supplier</label>
                    <SearchableSelect
                      options={(skus || []).length ? vendors.filter((v) => (skus || []).some((s) => s.vendor_id && s.vendor_id === v.id)) : vendors}
                      value={newItemForm.vendor_id}
                      onChange={(val) => setNewItemForm({ ...newItemForm, vendor_id: val })}
                      placeholder="-- Select Vendor (Optional) --"
                      getOptionLabel={(v) => v.name}
                      getOptionValue={(v) => v.id}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Unit Cost (₹)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newItemForm.unit_cost}
                        onChange={(e) => setNewItemForm({ ...newItemForm, unit_cost: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-semibold bg-white outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">MRP (₹)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newItemForm.mrp}
                        onChange={(e) => setNewItemForm({ ...newItemForm, mrp: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-semibold bg-white outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Selling Price (₹)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newItemForm.selling_price}
                        onChange={(e) => setNewItemForm({ ...newItemForm, selling_price: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-semibold bg-white outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Unit Price (₹)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newItemForm.unit_price}
                        onChange={(e) => setNewItemForm({ ...newItemForm, unit_price: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-semibold bg-white outline-none"
                      />
                    </div>
                  </div>

                  {/* Owner rule (20 Oct 2026): one main stock — no retail / store split. */}
                  <div className="pt-1">
                    <label className="block text-xs font-bold text-slate-700 mb-1">Opening Stock</label>
                    <input
                      type="number"
                      min="0"
                      value={newItemForm.store_qty}
                      onChange={(e) => setNewItemForm({ ...newItemForm, store_qty: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-semibold bg-white outline-none"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setIsMapModalOpen(false)}
                className="px-4 py-2 text-xs font-extrabold uppercase tracking-wider text-slate-600 hover:text-slate-900"
              >
                Cancel
              </button>
              {mappingMode === "link" ? (
                <button
                  type="button"
                  disabled={mapBusy || !selectedPosItem || !selectedSkuId}
                  onClick={handleSaveMapping}
                  className="lss-btn-gold px-6 py-2.5 uppercase tracking-wider text-xs font-extrabold disabled:opacity-50 shadow-md"
                >
                  {mapBusy ? "Saving Mapping…" : "Save & Map Item"}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={mapBusy || !selectedPosItem || !newItemForm.name.trim()}
                  onClick={handleCreateNewSkuAndMap}
                  className="lss-btn-gold px-6 py-2.5 uppercase tracking-wider text-xs font-extrabold disabled:opacity-50 shadow-md"
                >
                  {mapBusy ? "Registering Item…" : "Register SKU & Map"}
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}

      <div className="mt-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div>
            <div className="lss-overline">Recent Transactions</div>
            <h2 className="font-serif-lux text-2xl font-bold text-slate-950 mt-1">Transaction Preview</h2>
            {txns.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="px-2.5 py-1 bg-slate-100 text-slate-950 font-bold text-xs rounded-lg border border-slate-200">
                  Total Invoices: {new Set(txns.map(t => t.invoice_number)).size}
                </span>
                <span className="px-2.5 py-1 bg-amber-100/80 text-amber-950 font-extrabold text-xs rounded-lg border border-amber-300">
                  Total Qty: {txns.reduce((s, t) => s + (t.quantity || 1), 0)} Pcs
                </span>
                <span className="px-2.5 py-1 bg-blue-50 text-blue-900 font-bold text-xs rounded-lg border border-blue-200">
                  Services: {txns.filter(t => (t.type || "").toLowerCase() === "service").reduce((s, t) => s + (t.quantity || 1), 0)} Pcs
                </span>
                <span className="px-2.5 py-1 bg-purple-50 text-purple-900 font-bold text-xs rounded-lg border border-purple-200">
                  Products: {txns.filter(t => (t.type || "").toLowerCase() === "product").reduce((s, t) => s + (t.quantity || 1), 0)} Pcs
                </span>
              </div>
            )}
          </div>
          <DatePicker
            value={day || todayIST()}
            onChange={(e) => setDay(e.target.value)}
          />
        </div>

        <div className="lss-card overflow-x-auto border border-slate-200 rounded-xl shadow-xs">
          <table className="lss-table" data-testid="txns-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Client</th>
                <th>Item / Service</th>
                <th>Category</th>
                <th className="text-center">Qty</th>
                <th className="text-right">Rate</th>
                <th className="text-right">Discount</th>
                <th className="text-right">Net Price</th>
                <th className="text-right">Value Card</th>
                <th>Staff</th>
              </tr>
            </thead>
            <tbody>
              {txns.map((t) => (
                <tr key={t.id} className={t.is_quality_failure ? "bg-rose-50/70 border-l-4 border-rose-500" : ""}>
                  <td className="font-extrabold text-slate-950 flex items-center gap-1.5">
                    {t.is_quality_failure && <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />}
                    {t.invoice_number}
                  </td>
                  <td className="font-bold text-slate-900">{t.client_name}</td>
                  <td className="font-semibold text-slate-900">
                    <div className="flex items-center justify-between gap-2">
                      <span>{t.item_name}</span>
                      {t.type?.toLowerCase() === "product" && (
                        <button
                          onClick={() => openMapModal(t.item_name)}
                          className="px-2 py-0.5 bg-amber-100 hover:bg-amber-200 text-amber-950 text-[10px] font-extrabold uppercase rounded-md border border-amber-300 shrink-0 transition-all flex items-center gap-0.5"
                        >
                          <Link2 className="w-2.5 h-2.5 text-amber-700" /> Map
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="lss-badge bg-slate-100 text-slate-900 border border-slate-300 font-extrabold">
                      {t.category}
                    </span>
                  </td>
                  <td className="text-center font-extrabold text-slate-950">
                    <span className="px-2 py-0.5 bg-slate-100 rounded-md text-xs font-mono border border-slate-200">
                      {t.quantity || 1}
                    </span>
                  </td>
                  <td className="text-right tabular font-medium text-slate-700">{money(t.rate)}</td>
                  <td className="text-right tabular font-bold text-amber-800">{money(t.total_discount)}</td>
                  <td className="text-right tabular font-extrabold text-slate-950">{money(t.net_price)}</td>
                  <td className="text-right tabular font-bold text-amber-900">{money(t.value_card_paid || t.other || 0)}</td>
                  <td className="text-xs text-slate-700 font-semibold">
                    {t.staff?.map((s) => s.name).join(", ")}
                  </td>
                </tr>
              ))}
              {txns.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center text-slate-500 font-medium py-10">
                    No transactions found for {day || "selected date"}.
                  </td>
                </tr>
              )}
            </tbody>
            {txns.length > 0 && (
              <tfoot className="bg-slate-50 font-extrabold text-slate-950 border-t-2 border-slate-300 text-xs">
                <tr>
                  <td colSpan={4} className="text-right uppercase tracking-wider py-3 text-slate-600">Total Summary:</td>
                  <td className="text-center text-amber-950 text-sm font-extrabold">{txns.reduce((s, t) => s + (t.quantity || 1), 0)} Pcs</td>
                  <td className="text-right tabular">{money(txns.reduce((s, t) => s + (t.rate || 0), 0))}</td>
                  <td className="text-right tabular text-amber-800">{money(txns.reduce((s, t) => s + (t.total_discount || 0), 0))}</td>
                  <td className="text-right tabular text-emerald-800 text-sm font-black">{money(txns.reduce((s, t) => s + (t.net_price || 0), 0))}</td>
                  <td className="text-right tabular text-amber-900">{money(txns.reduce((s, t) => s + (t.value_card_paid || t.other || 0), 0))}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
